<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Palette, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-vue-next'
import { formatApiError, useApiClient } from '@yuksalish/api-client'
import { useAuthStore } from '@yuksalish/auth'
import PaginationControls from '../../components/PaginationControls.vue'
import {
  createReferenceItem,
  deleteReferenceItem,
  listColorItems,
  updateReferenceItem,
} from '../productService'
import type { ColorReference } from '../types'

const client = useApiClient()
const auth = useAuthStore()
const { t } = useI18n()
const colors = ref<ColorReference[]>([])
const loading = ref(false)
const saving = ref(false)
const deletingId = ref<number | null>(null)
const errorMessage = ref('')
const formError = ref('')
const search = ref('')
const editingId = ref<number | null>(null)
const currentPage = ref(1)
const pageSize = ref(10)
let searchTimer: ReturnType<typeof setTimeout> | null = null
const form = reactive({
  code: '',
  name: '',
  sort_order: '',
  is_active: '1',
})

const canUpdate = computed(() => auth.user?.permissions.includes('products.update') ?? false)
const canDelete = computed(() => auth.user?.permissions.includes('products.delete') ?? false)
const permissionHint = computed(() => t('engineering.colors.permissionHint'))
const filteredColors = computed(() => {
  const query = search.value.trim().toLocaleLowerCase()
  if (!query) return colors.value
  return colors.value.filter((item) =>
    [item.code, item.name].some((value) =>
      String(value ?? '').toLocaleLowerCase().includes(query),
    ),
  )
})
const visibleColors = computed(() =>
  [...filteredColors.value].sort((left, right) => {
    const orderDelta = (left.sort_order ?? 0) - (right.sort_order ?? 0)
    if (orderDelta !== 0) return orderDelta
    return left.name.localeCompare(right.name)
  }),
)
const totalPages = computed(() =>
  Math.max(1, Math.ceil(visibleColors.value.length / pageSize.value)),
)
const paginatedColors = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value
  return visibleColors.value.slice(start, start + pageSize.value)
})
const isEditing = computed(() => editingId.value != null)

function resetForm(): void {
  editingId.value = null
  form.code = ''
  form.name = ''
  form.sort_order = ''
  form.is_active = '1'
  formError.value = ''
}

function startEdit(item: ColorReference): void {
  editingId.value = item.id
  form.code = item.code
  form.name = item.name
  form.sort_order = item.sort_order ? String(item.sort_order) : ''
  form.is_active = item.is_active ? '1' : '0'
  formError.value = ''
}

async function loadColors(): Promise<void> {
  loading.value = true
  errorMessage.value = ''
  try {
    colors.value = await listColorItems(client)
  } catch (error: unknown) {
    colors.value = []
    errorMessage.value = formatApiError(error)
  } finally {
    loading.value = false
  }
}

async function submit(): Promise<void> {
  if (!canUpdate.value) return
  saving.value = true
  formError.value = ''
  try {
    const payload = {
      code: form.code.trim(),
      name: form.name.trim(),
      sort_order: form.sort_order.trim() ? Number(form.sort_order) : 0,
      is_active: Number(form.is_active),
    }
    if (editingId.value == null) {
      await createReferenceItem(client, 'color', payload)
    } else {
      await updateReferenceItem(client, 'color', editingId.value, payload)
    }
    await loadColors()
    resetForm()
  } catch (error: unknown) {
    formError.value = formatApiError(error)
  } finally {
    saving.value = false
  }
}

async function removeColor(item: ColorReference): Promise<void> {
  if (!canDelete.value) return
  if (!window.confirm(t('engineering.colors.confirmDelete', { name: item.name }))) return
  deletingId.value = item.id
  errorMessage.value = ''
  try {
    await deleteReferenceItem(client, 'color', item.id)
    if (editingId.value === item.id) {
      resetForm()
    }
    await loadColors()
  } catch (error: unknown) {
    errorMessage.value = formatApiError(error)
  } finally {
    deletingId.value = null
  }
}

onMounted(loadColors)
watch(search, () => {
  currentPage.value = 1
  if (searchTimer) clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    currentPage.value = Math.min(currentPage.value, totalPages.value)
  }, 300)
})
watch([visibleColors, pageSize], () => {
  currentPage.value = Math.min(currentPage.value, totalPages.value)
})
onBeforeUnmount(() => {
  if (searchTimer) clearTimeout(searchTimer)
})
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('engineering.colors.eyebrow') }}</p>
      <h2>{{ t('engineering.colors.title') }}</h2>
      <p>{{ t('engineering.colors.description') }}</p>
    </div>
    <div class="summary-tile">
      <Palette :size="18" aria-hidden="true" />
      <div>
        <strong>{{ colors.length }}</strong>
        <span>{{ t('engineering.colors.count') }}</span>
      </div>
    </div>
  </div>

  <section class="workspace">
    <form class="editor-card" @submit.prevent="submit">
      <div class="section-heading">
        <div>
          <p class="eyebrow">{{ isEditing ? t('engineering.colors.editEyebrow') : t('engineering.colors.createEyebrow') }}</p>
          <h3>{{ isEditing ? t('engineering.colors.editTitle') : t('engineering.colors.createTitle') }}</h3>
        </div>
        <button v-if="isEditing" class="ghost-button" type="button" @click="resetForm">
          {{ t('engineering.action.cancel') }}
        </button>
      </div>

      <p v-if="formError" class="message message--error" role="alert">{{ formError }}</p>
      <p v-if="!canUpdate" class="message message--hint">{{ permissionHint }}</p>

      <div class="form-grid">
        <label>
          <span>{{ t('engineering.colors.code') }}</span>
          <input v-model="form.code" :disabled="saving || !canUpdate" type="text">
        </label>
        <label>
          <span>{{ t('engineering.colors.name') }}</span>
          <input v-model="form.name" :disabled="saving || !canUpdate" type="text">
        </label>
        <label>
          <span>{{ t('engineering.colors.sortOrder') }}</span>
          <input v-model="form.sort_order" :disabled="saving || !canUpdate" min="0" step="1" type="number">
        </label>
        <label>
          <span>{{ t('engineering.colors.status') }}</span>
          <select v-model="form.is_active" :disabled="saving || !canUpdate">
            <option value="1">{{ t('engineering.colors.active') }}</option>
            <option value="0">{{ t('engineering.colors.inactive') }}</option>
          </select>
        </label>
      </div>

      <div class="form-actions">
        <button
          class="primary-button"
          type="submit"
          :disabled="saving || !canUpdate || !form.code.trim() || !form.name.trim()"
          :title="!canUpdate ? permissionHint : undefined"
        >
          <Plus v-if="!isEditing" :size="16" aria-hidden="true" />
          <Pencil v-else :size="16" aria-hidden="true" />
          {{ saving ? t('engineering.colors.saving') : (isEditing ? t('engineering.colors.saveChanges') : t('engineering.colors.create')) }}
        </button>
      </div>
    </form>

    <section class="list-card">
      <div class="list-toolbar">
        <label class="search-field">
          <span>{{ t('engineering.colors.search') }}</span>
          <input v-model="search" type="search" :placeholder="t('engineering.colors.searchPlaceholder')">
        </label>
        <button class="ghost-button" type="button" :disabled="loading" @click="loadColors">
          <RefreshCw :size="16" aria-hidden="true" />
          {{ t('engineering.action.retry') }}
        </button>
      </div>

      <div v-if="errorMessage" class="message message--error" role="alert">{{ errorMessage }}</div>
      <div v-if="loading" class="table-state">{{ t('engineering.colors.loading') }}</div>
      <div v-else-if="!visibleColors.length" class="table-state">{{ t('engineering.colors.empty') }}</div>
      <table v-else class="reference-table">
        <thead>
          <tr>
            <th>{{ t('engineering.colors.code') }}</th>
            <th>{{ t('engineering.colors.name') }}</th>
            <th>{{ t('engineering.colors.sortOrder') }}</th>
            <th>{{ t('engineering.colors.status') }}</th>
            <th>{{ t('engineering.catalog.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in paginatedColors" :key="item.id">
            <td>
              <strong>{{ item.code }}</strong>
              <small>#{{ item.id }}</small>
            </td>
            <td>{{ item.name }}</td>
            <td>{{ item.sort_order }}</td>
            <td>
              <span class="status" :class="item.is_active ? 'status--ready' : 'status--muted'">
                {{ item.is_active ? t('engineering.colors.active') : t('engineering.colors.inactive') }}
              </span>
            </td>
            <td class="row-actions">
              <button class="icon-button" type="button" :disabled="!canUpdate" :title="!canUpdate ? permissionHint : undefined" @click="startEdit(item)">
                <Pencil :size="16" aria-hidden="true" />
                {{ t('engineering.colors.edit') }}
              </button>
              <button class="icon-button icon-button--danger" type="button" :disabled="!canDelete || deletingId === item.id" :title="!canDelete ? permissionHint : undefined" @click="removeColor(item)">
                <Trash2 :size="16" aria-hidden="true" />
                {{ t('engineering.colors.remove') }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <PaginationControls
        v-if="!loading && visibleColors.length"
        v-model:page="currentPage"
        v-model:page-size="pageSize"
        :total="visibleColors.length"
        :page-size-options="[10, 25, 50]"
      />
    </section>
  </section>
</template>

<style scoped>
.page-heading,.section-heading,.list-toolbar,.form-actions,.row-actions{display:flex;gap:12px;align-items:center;justify-content:space-between}
.page-heading{margin-bottom:24px}
.workspace{display:grid;grid-template-columns:minmax(320px,380px) minmax(0,1fr);gap:18px}
.editor-card,.list-card{padding:18px;border:1px solid #d7dfdc;border-radius:8px;background:#fff}
.eyebrow{margin:0 0 4px;color:#66726e;font-size:12px;font-weight:700;text-transform:uppercase}
h2,h3,p{margin:0}
.page-heading>div:first-child p:last-child{margin-top:8px;color:#66726e}
.summary-tile{display:inline-flex;gap:12px;align-items:center;padding:12px 14px;border:1px solid #d7dfdc;border-radius:8px;background:#fff}
.summary-tile strong,td strong{display:block}
.summary-tile span,td small{color:#66726e;font-size:12px}
.form-grid{display:grid;gap:12px;margin-top:16px}
label{display:grid;gap:6px}
label span,th{color:#586660;font-size:12px;font-weight:700;text-transform:uppercase}
input,select,button{min-height:40px;padding:8px 10px;border:1px solid #adb9b4;border-radius:8px;background:#fff;font:inherit}
.primary-button,.ghost-button,.icon-button{display:inline-flex;gap:8px;align-items:center;justify-content:center;width:auto;font-weight:700;cursor:pointer}
.primary-button{border:0;color:#fff;background:#3568a8}
.ghost-button,.icon-button{color:#285b9a}
.icon-button--danger{color:#8b3025}
.form-actions{justify-content:flex-start;margin-top:16px}
.search-field{flex:1}
.search-field input{width:100%}
.message{margin-top:16px;padding:12px 14px;border-radius:8px}
.message--error{border:1px solid #d7a8a1;color:#852d22;background:#fff5f3}
.message--hint{border:1px solid #d7dfdc;color:#66726e;background:#f7faf9}
.list-card table{width:100%;margin-top:16px;border-collapse:collapse}
th,td{padding:12px;border-bottom:1px solid #e1e6e4;text-align:left;vertical-align:middle}
th{background:#f3f6f5}
.status{display:inline-flex;padding:4px 8px;border:1px solid;border-radius:999px;font-size:12px;font-weight:700}
.status--ready{border-color:#86b4a4;color:#236451;background:#eff8f5}
.status--muted{border-color:#c2c9c6;color:#5f6b66;background:#f5f7f6}
.table-state{display:grid;min-height:220px;color:#697671;place-items:center}
button:disabled{cursor:not-allowed;opacity:.55}
@media (max-width:900px){.workspace{grid-template-columns:1fr}}
@media (max-width:640px){.page-heading,.section-heading,.list-toolbar,.row-actions{align-items:stretch;flex-direction:column}.row-actions{white-space:nowrap}}
</style>
